import type { AiFix } from '../../../api/questionQualityApi';
import './QuestionQuality.css';

/**
 * What the AI did to this question, on the finding it was asked to fix.
 *
 * A finding sitting at "Review pending after AI update" is asking the reader for
 * a decision, and the decision needs three things: what changed, why, and
 * whether anyone other than the model that proposed it thinks it is right. The
 * status alone gives none of them — without this card a reviewer is looking at
 * an admin edit with no explanation and has to reconstruct the rest.
 *
 * It renders `aiFix`, written by `scripts/areas/quality/quality-apply-fixes.ts`. The
 * card is deliberately not a diff: the diff is the revision, shown by the
 * History panel on this same page, and duplicating it here would give the reader
 * two accounts of one edit that can disagree.
 *
 * See docs/FeatureDesigns/question-quality/ai-fix-loop-target.md §9.
 */
export function AiFixCard({ fix }: { fix: AiFix }) {
    /**
     * The check verdict leads, because it is the only line here the proposing
     * model did not write. "agree" means a second agent solved the question from
     * scratch and landed on the same answer; anything else is a reason to look
     * harder, and `disagree` should never have reached a reviewer at all — the
     * apply step refuses those.
     */
    const verdictClass = fix.checkVerdict === 'agree' ? 'qq-aifix-ok'
        : fix.checkVerdict ? 'qq-aifix-doubt' : 'qq-dim';

    return (
        <div className="qq-aifix">
            <header>
                <b>AI fix</b>
                <span className={`qq-aifix-outcome qq-aifix-${fix.outcome}`}>{fix.outcome}</span>
                {fix.checkVerdict && (
                    <span className={verdictClass}>
                        second opinion: {fix.checkVerdict}
                    </span>
                )}
                <span className="qq-dim">
                    {fix.model} · {new Date(fix.attemptedAt).toLocaleDateString()}
                    {fix.revisionSeq !== undefined ? ` · revision #${fix.revisionSeq}` : ''}
                </span>
            </header>

            <p className="qq-aifix-note">{fix.changeNote}</p>

            {fix.changedFields.length > 0 && (
                <p className="qq-dim qq-aifix-fields">
                    changed: {fix.changedFields.map(f => <code key={f}>{f}</code>).reduce<React.ReactNode[]>(
                        (acc, el, i) => (i === 0 ? [el] : [...acc, ', ', el]), [])}
                    {fix.revisionSeq !== undefined && ' — see History below for the before/after'}
                </p>
            )}

            {/* Collapsed: the reviewer's first question is "what changed", not
                "how did it reason". The working matters when they disagree with
                the result, which is the minority case and worth one click. */}
            {fix.rationale && (
                <details className="qq-aifix-why">
                    <summary>How the AI arrived at this</summary>
                    <p>{fix.rationale}</p>
                </details>
            )}
        </div>
    );
}
